import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useMe } from '@/hooks/useMe';
import { cn } from '@/lib/utils';
import { DangerTriangleSolid } from '@mynaui/icons-react';
import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

export const EmailVerificationForm = ({ className, ...props }: React.ComponentProps<'div'>) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { me } = useMe();
  const { t } = useTranslation('auth');

  // Get token from URL params
  const token = searchParams.get('token');
  const id = searchParams.get('id');

  const verifyEmailQuery = useQuery({
    queryKey: ['verify-email', token],
    queryFn: async () => {
      if (!token) {
        throw new Error('No verification token provided');
      }

      const response = await fetch(`${import.meta.env.VITE_ENDPOINT}/verify-email?token=${token}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token,
          id,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Email verification failed');
      }

      if (!data.success) {
        throw new Error('Invalid response from server');
      }

      return data;
    },
    enabled: !!token, // Only run when token is available
    retry: false,
    // The verification token is single-use; never re-run after the first attempt.
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    staleTime: Infinity,
    gcTime: Infinity,
  });

  React.useEffect(() => {
    if (verifyEmailQuery.isSuccess) {
      navigate('/auth/email/verified', { replace: true });
    }
  }, [verifyEmailQuery.isSuccess, navigate]);

  React.useEffect(() => {
    if (verifyEmailQuery.isError) {
      const errorMessage =
        verifyEmailQuery.error instanceof Error ? verifyEmailQuery.error.message : 'Email verification failed';
      toast.error(t('emailVerification.emailVerificationFailed'), {
        description: errorMessage,
      });
    }
  }, [verifyEmailQuery.isError, verifyEmailQuery.error]);

  const cta = (
    <Button variant='ghost' onClick={() => navigate(me?.email ? '/' : '/auth/login')} className='mt-4'>
      {me?.email ? t('emailVerification.backToDashboard') : t('emailVerification.backToLogin')}
    </Button>
  );
  return (
    <div className={cn('flex flex-col gap-6 bg-white', className)} {...props}>
      <Card className='overflow-hidden p-0'>
        <CardContent className='p-6 md:p-8'>
          <div className='flex flex-col items-center text-center gap-6'>
            {(verifyEmailQuery.isLoading || verifyEmailQuery.isSuccess) && (
              <>
                <div className='flex flex-col items-center'>
                  <Loader2 className='size-8 animate-spin' />
                  <h1 className='text text-slate-900 mt-4'>{t('emailVerification.updatingEmail')}</h1>
                  <p className='text-slate-500 text-balance text-sm'>
                    {t('emailVerification.updatingDescription')}
                  </p>
                </div>
              </>
            )}

            {verifyEmailQuery.isError && (
              <>
                <div className='flex flex-col items-center'>
                  <div className='size-20 bg-rose-50 rounded-full flex items-center justify-center pb-1'>
                    <DangerTriangleSolid className='size-12 text-rose-600' />
                  </div>
                  <h1 className='font-semibold text-rose-600 mt-4'>{t('emailVerification.failedToUpdate')}</h1>
                  <p className='text-sm text-slate-500 mt-1'>
                    {t('emailVerification.failedDescription')}
                  </p>
                  {cta}
                </div>
              </>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
